import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  ArrowRight,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Eye,
  EyeOff,
  GraduationCap,
  Loader2,
  Lock,
  MapPin,
  Megaphone,
  User,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import schoolCrest from '../assets/logos/crest.png'
import { api } from '../services/api'
import { SCHOOL_SLOGAN, slidesFor, type HighlightSlide } from '../components/sign-in/signInSlides'
import { CornerStars, RuledPage, Star, StarField, Swoosh } from '../components/sign-in/BrandDecor'

const SCHOOL_NAME = 'The Promise Standard School'
// Parents and students sign in here too, so not "administration".
const SCHOOL_TAGLINE = 'School Portal'

/**
 * Destinations for the links under the sign-in card. A link shows only once
 * it has a URL here; internal paths ('/help') and external URLs
 * ('https://...') both work.
 */
const PORTAL_LINKS: Record<string, string | null> = {
  publicWebsite: null,
  staffDirectory: null,
}

/**
 * Campus photos for the left panel, read from src/assets/campus at build time.
 * Drop files in that folder and they appear here — see its README. They sit
 * behind the navy wash; with none, the panel is gradient and crest only.
 */
const campusModules = import.meta.glob('../assets/campus/*.{jpg,jpeg,png,webp,avif}', {
  eager: true,
  query: '?url',
  import: 'default',
})
const campusImages = Object.keys(campusModules)
  .sort()
  .map((path) => campusModules[path] as string)

const SLIDE_INTERVAL_MS = 7000
const NOTICE_INTERVAL_MS = 8000

const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
})

type LoginForm = z.infer<typeof loginSchema>

// Brand colours, from the crest.
const GOLD = '#ebcd7c'
const MAROON = '#8c1d18'

const fieldClasses = (hasError: boolean) =>
  `w-full rounded-xl border bg-[#fbf9f5] py-2.5 pl-10 pr-4 text-sm text-[#12233f] outline-none transition placeholder:text-slate-400 focus:bg-white focus:ring-4 tall:py-3 ${
    hasError
      ? 'border-rose-500 focus:border-rose-500 focus:ring-rose-500/15'
      : 'border-[#e3dccd] hover:border-[#d6ccb8] focus:border-[#8c1d18] focus:ring-[#8c1d18]/10'
  }`

export default function Login() {
  const [showPassword, setShowPassword] = useState(false)
  const [activeNotice, setActiveNotice] = useState(0)
  const [paused, setPaused] = useState(false)
  const [activeSlide, setActiveSlide] = useState(0)
  const { login, error } = useAuthStore()
  // The school's noticeboard: term dates, the fee deadline, and the events and
  // notices staff ticked for this page. Public, so dates and titles only.
  const highlights = useQuery({
    queryKey: ['sign-in-highlights'],
    queryFn: async () => (await api.get<{ slides: HighlightSlide[] }>('/auth/sign-in-highlights/')).data.slides,
    staleTime: 5 * 60_000,
    retry: false,
  })
  const notices = slidesFor(highlights.data)
  const notice = notices[Math.min(activeNotice, notices.length - 1)]

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  })

  // Cross-fade the campus photos. Skipped for a single image, and for anyone
  // who has asked their system to reduce motion.
  useEffect(() => {
    if (campusImages.length < 2) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % campusImages.length)
    }, SLIDE_INTERVAL_MS)

    return () => window.clearInterval(timer)
  }, [])

  const onSubmit = async ({ username, password }: LoginForm) => {
    try {
      await login(username, password)
    } catch {
      // The store already surfaces the failure through `error`.
    }
  }

  // Move through the notices on their own, holding still while someone reads
  // one (pointer or keyboard focus on it) or has asked for reduced motion.
  useEffect(() => {
    if (notices.length < 2 || paused) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const timer = window.setInterval(() => setActiveNotice((current) => (current + 1) % notices.length), NOTICE_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [notices.length, paused])

  const previousNotice = () => setActiveNotice((current) => (current - 1 + notices.length) % notices.length)
  const nextNotice = () => setActiveNotice((current) => (current + 1) % notices.length)
  const NoticeIcon = notice.kind === 'notice' ? Megaphone : notice.kind === 'admissions' ? GraduationCap : CalendarDays

  return (
    <div className="min-h-screen bg-[#0c1b33] text-[#12233f] antialiased">
      <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
        {/* The story panel. Sticky so it stays put if the sign-in side scrolls. */}
        <section className="relative hidden flex-col overflow-hidden bg-[#0c1b33] px-8 py-7 text-white lg:flex lg:sticky lg:top-0 lg:h-screen xl:px-12 tall:py-12">
          <div className="absolute inset-0 z-0">
            {campusImages.map((src, index) => (
              <img
                key={src}
                src={src}
                alt=""
                aria-hidden="true"
                className={`absolute inset-0 h-full w-full scale-105 object-cover object-center transition-opacity duration-1000 ${
                  index === activeSlide ? 'opacity-25' : 'opacity-0'
                }`}
              />
            ))}
            {/* Navy, warmed by a gold glow from the top and a maroon one from below. */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  `radial-gradient(ellipse 60% 55% at 12% 0%, rgba(235,205,124,0.16), transparent 70%),` +
                  `radial-gradient(ellipse 70% 60% at 100% 100%, rgba(140,29,24,0.55), transparent 70%),` +
                  `linear-gradient(150deg, rgba(22,41,77,0.92) 0%, rgba(15,31,61,0.94) 45%, rgba(8,17,31,0.97) 100%)`,
              }}
            />
            <RuledPage />
            <StarField />
            {/* The crest, large and faint, like a watermark. */}
            <img
              src={schoolCrest}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute -right-24 top-1/2 w-[36rem] -translate-y-1/2 opacity-[0.07] mix-blend-luminosity"
            />
          </div>
          {/* The spine where the two halves meet, like a book's binding. */}
          <div
            aria-hidden="true"
            className="absolute inset-y-0 right-0 z-20 w-1.5"
            style={{ background: `linear-gradient(to bottom, ${GOLD}, ${MAROON} 45%, ${MAROON} 55%, ${GOLD})` }}
          />

          <header className="relative z-10 flex shrink-0 items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 p-1.5 shadow-lg ring-2 ring-[#ebcd7c]/70">
              <img src={schoolCrest} alt="" aria-hidden="true" className="h-full w-full object-contain" />
            </span>
            <div>
              <p className="text-lg font-extrabold uppercase tracking-[0.1em] text-white xl:text-xl">{SCHOOL_NAME}</p>
              <p className="flex items-center gap-2 text-sm font-semibold text-[#ebcd7c]">
                <Star className="h-3 w-3" />
                {SCHOOL_TAGLINE}
              </p>
            </div>
          </header>

          {/* Takes the height left between the header and the footer, so the
              footer is always on screen whatever the slide. */}
          <div className="relative z-10 flex min-h-0 max-w-2xl flex-1 flex-col justify-center py-5">
            <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-[#ebcd7c]">
              <span className="h-px w-10 bg-gradient-to-r from-transparent to-[#ebcd7c]" aria-hidden="true" />
              Our promise
            </p>
            <h1 className="mt-3 text-[2.6rem] font-extrabold leading-[1.06] tracking-tight text-white tall:text-6xl 2xl:tall:text-7xl">
              Rewriting the{' '}
              <span className="relative inline-block text-[#ebcd7c]">
                story
                <Swoosh className="absolute -bottom-2 left-0 h-3 w-full text-[#ebcd7c]/80 tall:-bottom-3 tall:h-4" />
              </span>
              <br />
              of illiteracy.
            </h1>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-300 tall:mt-6 xl:text-base">
              One school portal for learning, fees and school life, for staff, parents and students alike.
            </p>

            <section
              aria-roledescription="carousel"
              aria-label="School noticeboard"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              onFocus={() => setPaused(true)}
              onBlur={() => setPaused(false)}
              className="relative mt-6 max-w-xl overflow-hidden rounded-2xl border border-white/10 bg-[#0a1628]/75 p-5 shadow-2xl shadow-black/40 backdrop-blur-md tall:mt-9 xl:p-6"
            >
              <div aria-hidden="true" className="absolute inset-x-0 top-0 h-[3px]" style={{ background: `linear-gradient(to right, ${GOLD}, ${MAROON}, ${GOLD})` }} />

              {/* The same height on every slide, so nothing below it jumps as they change. */}
              <div key={notice.key} aria-live="polite" className="flex h-[7.5rem] items-start gap-5 overflow-hidden tall:h-[8.5rem]">
                {notice.chip ? (
                  <div className="flex h-[4.5rem] w-16 shrink-0 flex-col items-center justify-center overflow-hidden rounded-xl bg-[#ebcd7c] text-[#0f1f3d] shadow-lg shadow-black/30">
                    <span className="w-full bg-[#8c1d18] py-0.5 text-center text-[10px] font-bold uppercase tracking-widest text-[#f0d9a8]">{notice.chip.month}</span>
                    <span className="flex flex-1 items-center text-[1.7rem] font-extrabold leading-none tracking-tight">{notice.chip.day}</span>
                  </div>
                ) : notice.kind === 'brand' ? (
                  <div className="flex h-[4.5rem] w-16 shrink-0 items-center justify-center rounded-xl bg-white p-1.5 shadow-lg shadow-black/30 ring-2 ring-[#ebcd7c]/70">
                    <img src={schoolCrest} alt="" aria-hidden="true" className="h-full w-full object-contain" />
                  </div>
                ) : (
                  <div className="flex h-[4.5rem] w-16 shrink-0 items-center justify-center rounded-xl bg-[#ebcd7c] text-[#0f1f3d] shadow-lg shadow-black/30">
                    <NoticeIcon className="h-7 w-7" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#ebcd7c]">{notice.eyebrow}</p>
                    {notices.length > 1 && (
                      <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-slate-300">
                        {activeNotice + 1} / {notices.length}
                      </span>
                    )}
                  </div>
                  <h2 className="mt-1 line-clamp-2 text-lg font-bold leading-snug tracking-tight text-white">{notice.title}</h2>
                  {notice.body && <p className="mt-1 line-clamp-2 text-sm leading-6 text-slate-300">{notice.body}</p>}
                </div>
              </div>

              {/* Where and when on the left, the way through the slides on the right: one row. */}
              <div className="mt-4 flex min-h-[2.75rem] flex-wrap items-center justify-between gap-x-6 gap-y-3 border-t border-white/10 pt-4">
                <div className="flex min-w-0 flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-slate-300">
                  {notice.link && (
                    <Link
                      to={notice.link.to}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-[#ebcd7c] px-3.5 py-2 text-sm font-bold text-[#0f1f3d] shadow-sm transition hover:bg-[#f0d9a8] focus:outline-none focus:ring-2 focus:ring-[#ebcd7c]/60 focus:ring-offset-2 focus:ring-offset-[#0f1f3d]"
                    >
                      {notice.link.label}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  )}
                  {notice.meta.map((line) => {
                    const Icon = line.icon === 'clock' ? Clock3 : line.icon === 'place' ? MapPin : CalendarDays
                    return (
                      <span key={line.text} className="inline-flex items-center gap-2">
                        <Icon className="h-4 w-4 text-[#ebcd7c]" />
                        {line.text}
                      </span>
                    )
                  })}
                </div>
                {notices.length > 1 && (
                  <div className="ml-auto flex items-center gap-3">
                    <div className="flex gap-1.5" role="group" aria-label="Choose a notice">
                      {notices.map((item, index) => (
                        <button
                          key={item.key}
                          type="button"
                          aria-label={`Show notice ${index + 1}: ${item.title}`}
                          aria-current={index === activeNotice}
                          onClick={() => setActiveNotice(index)}
                          className={`h-2 rounded-full transition-all duration-300 ${
                            index === activeNotice ? 'w-6 bg-[#ebcd7c]' : 'w-2 bg-white/30 hover:bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={previousNotice}
                      aria-label="Previous notice"
                      className="rounded-full border border-white/20 bg-white/5 p-2 text-slate-300 transition hover:border-[#ebcd7c]/60 hover:text-[#ebcd7c] active:scale-95"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={nextNotice}
                      aria-label="Next notice"
                      className="rounded-full border border-white/20 bg-white/5 p-2 text-slate-300 transition hover:border-[#ebcd7c]/60 hover:text-[#ebcd7c] active:scale-95"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Fills over one slide's time and starts again on the next; holds while the card is being read. */}
              {notices.length > 1 && (
                <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[2px] bg-white/5 motion-reduce:hidden">
                  <div
                    key={`${notice.key}-${activeNotice}`}
                    className="h-full origin-left bg-[#ebcd7c]/80"
                    style={{
                      animation: `notice-progress ${NOTICE_INTERVAL_MS}ms linear forwards`,
                      animationPlayState: paused ? 'paused' : 'running',
                    }}
                  />
                </div>
              )}
            </section>
          </div>

          <footer className="relative z-10 flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-t border-white/10 pt-5 text-sm text-slate-400">
            <span className="font-bold uppercase tracking-[0.1em] text-slate-200">{SCHOOL_NAME}</span>
            <Star className="h-2.5 w-2.5 text-[#df7268]" />
            <span className="font-medium italic text-[#ebcd7c]">{SCHOOL_SLOGAN}</span>
            <Star className="h-2.5 w-2.5 text-[#df7268]" />
            <span>© {new Date().getFullYear()}</span>
          </footer>
        </section>

        {/* Sign-in panel: cream paper, the crest held up like a seal. */}
        <main
          className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#f5f2ec] px-5 py-3 sm:px-8 lg:px-10 xl:px-16 tall:py-10"
          style={{
            backgroundImage:
              `radial-gradient(ellipse 55% 45% at 100% 0%, rgba(235,205,124,0.22), transparent 70%),` +
              `radial-gradient(ellipse 50% 40% at 0% 100%, rgba(140,29,24,0.08), transparent 70%),` +
              `radial-gradient(rgba(15,31,61,0.07) 1px, transparent 1px)`,
            backgroundSize: 'auto, auto, 22px 22px',
          }}
        >
          <CornerStars />
          <div className="relative w-full max-w-lg">
            {/* A warm halo behind the crest. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-0 h-64 w-80 -translate-x-1/2 rounded-full blur-2xl tall:h-72 tall:w-96"
              style={{ background: 'radial-gradient(closest-side, rgba(235,205,124,0.55), rgba(235,205,124,0) 100%)' }}
            />
            <img
              src={schoolCrest}
              alt={`${SCHOOL_NAME} crest`}
              className="relative z-10 mx-auto -mb-44 h-64 w-64 object-contain drop-shadow-[0_10px_18px_rgba(15,31,61,0.25)] tall:-mb-48 tall:h-72 tall:w-72"
            />

            <div className="relative overflow-hidden rounded-3xl border border-[#e8e1d3] bg-white px-6 pb-6 pt-[11.5rem] shadow-[0_24px_60px_-20px_rgba(15,31,61,0.35)] sm:px-9 tall:pb-7 tall:pt-[12.75rem]">
              <div aria-hidden="true" className="absolute inset-x-0 top-0 h-1.5" style={{ background: `linear-gradient(to right, ${MAROON}, ${GOLD} 50%, ${MAROON})` }} />

              <div className="text-center">
                <h2 className="text-xl font-extrabold tracking-tight text-[#0f1f3d] tall:sm:text-2xl tall:xl:text-[1.7rem]">
                  Welcome to {SCHOOL_NAME}
                </h2>
                {/* The story panel is hidden on phones, so the slogan rides here too. */}
                <p className="mt-1 text-sm font-semibold italic text-[#8c1d18] lg:hidden">{SCHOOL_SLOGAN}</p>
                <p className="mt-1.5 flex items-center justify-center gap-2 text-sm text-slate-500">
                  <span className="hidden h-px w-6 shrink-0 bg-[#ebcd7c] sm:block" aria-hidden="true" />
                  Secure access for staff, parents and students.
                  <span className="hidden h-px w-6 shrink-0 bg-[#ebcd7c] sm:block" aria-hidden="true" />
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-3 tall:mt-6 tall:space-y-4" noValidate>
                {error && (
                  <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800">
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="username" className="mb-1.5 block text-sm font-semibold text-[#12233f]">
                    Username
                  </label>
                  <div className="relative">
                    <User className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#b08d3f]" />
                    <input
                      {...register('username')}
                      id="username"
                      type="text"
                      autoComplete="username"
                      placeholder="Enter your username or email"
                      aria-invalid={Boolean(errors.username)}
                      className={fieldClasses(Boolean(errors.username))}
                    />
                  </div>
                  {errors.username?.message && <p className="mt-1 text-xs font-medium text-rose-600">{errors.username.message}</p>}
                </div>

                <div>
                  <div className="mb-1.5 flex items-baseline justify-between">
                    <label htmlFor="password" className="block text-sm font-semibold text-[#12233f]">
                      Password
                    </label>
                    <Link to="/forgot-password" className="text-xs font-semibold text-[#8c1d18] hover:text-[#6f1613] hover:underline">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#b08d3f]" />
                    <input
                      {...register('password')}
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      aria-invalid={Boolean(errors.password)}
                      className={`${fieldClasses(Boolean(errors.password))} pr-11`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((current) => !current)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute inset-y-0 right-0 flex items-center px-3.5 text-slate-400 transition hover:text-[#0f1f3d]"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password?.message && <p className="mt-1 text-xs font-medium text-rose-600">{errors.password.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group relative mt-1 flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-[#8c1d18] via-[#7a1915] to-[#5e120f] px-5 py-3.5 text-sm font-bold tracking-wide text-[#f0d9a8] shadow-lg shadow-[#8c1d18]/25 ring-1 ring-inset ring-[#ebcd7c]/30 transition-all duration-200 hover:shadow-xl hover:shadow-[#8c1d18]/30 focus:outline-none focus:ring-4 focus:ring-[#8c1d18]/25 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {/* A slow gold sheen across the button on hover. */}
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-[#ebcd7c]/25 to-transparent opacity-0 transition-all duration-700 group-hover:left-full group-hover:opacity-100 motion-reduce:hidden"
                  />
                  {isSubmitting ? (
                    <>
                      Signing in…
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </>
                  ) : (
                    <>
                      Sign in
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </form>

              {/* Where there is room, a way in for families who are not with us yet. */}
              <p className="mt-6 hidden text-center text-sm text-slate-500 tall:block">
                New to the school?{' '}
                <Link to="/apply" className="font-semibold text-[#8c1d18] hover:underline">
                  Apply for a place
                </Link>
              </p>
            </div>

            {/* Only links that go somewhere: an inert, greyed-out link is just noise. */}
            {(PORTAL_LINKS.publicWebsite || PORTAL_LINKS.staffDirectory) && (
              <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-500 tall:mt-6">
                {PORTAL_LINKS.publicWebsite && (
                  <a href={PORTAL_LINKS.publicWebsite} className="underline underline-offset-2 hover:text-[#12233f]">
                    {SCHOOL_NAME} public website
                  </a>
                )}
                {PORTAL_LINKS.staffDirectory && (
                  <a href={PORTAL_LINKS.staffDirectory} className="underline underline-offset-2 hover:text-[#12233f]">
                    Staff directory
                  </a>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}
